import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import type { CaptureForm } from '../../models/captureForms'
import {
  createAdminCaptureForm,
  deleteAdminCaptureForm,
  deleteCapture,
  getCapture,
  getCaptureForm,
  listAdminCaptureForms,
  listCaptureForms,
  submitCapture,
  updateAdminCaptureForm,
  updateCapture,
} from './client'
import type {
  AdminCaptureFormInput,
  CaptureSubmitPayload,
} from '../../models/captureForms'

export const CAPTURE_FORMS_KEY = ['capture-forms'] as const
export const captureFormKey = (id: number) => ['capture-forms', id] as const

export function useGetCaptureForms() {
  return useQuery({
    queryKey: CAPTURE_FORMS_KEY,
    queryFn: () => listCaptureForms(),
  })
}

/**
 * Derive the "which sales stage is gated by which capture form" map.
 *
 * Pure so the gating semantics can be unit-tested without rendering a hook.
 *
 * @param captureForms - The company's capture forms, or undefined while loading.
 * @param enabled - Whether the capture-forms feature flag is on.
 * @returns Map keyed by SalesStage id → the CaptureForm that gates it. Empty
 *   when the flag is off or forms haven't loaded.
 */
export function buildGatedStageMap(
  captureForms: CaptureForm[] | undefined,
  enabled: boolean
): Map<number, CaptureForm> {
  const map = new Map<number, CaptureForm>()
  if (!enabled || !captureForms) return map
  for (const form of captureForms) {
    for (const binding of form.bindings) {
      if (binding.trigger !== 'stage_gated') continue
      for (const stageId of binding.gating_stages) {
        map.set(stageId, form)
      }
    }
  }
  return map
}

/**
 * Build the gated-stage map, shared by every stage-change entry point (deal
 * detail, kanban, bulk select modal) so the subtle gating derivation lives in
 * exactly one place.
 *
 * @returns `gatedStageMap` (see {@link buildGatedStageMap}) and `isLoading` —
 *   true only while the flag is on and the forms are still fetching. Callers
 *   should treat `isLoading` as "gating unknown" and refuse the stage change
 *   until it resolves, so a move can't slip through ungated during the fetch.
 */
export function useGatedStageMap(): {
  gatedStageMap: Map<number, CaptureForm>
  isLoading: boolean
} {
  const { data: captureForms, isLoading } = useGetCaptureForms()
  const { enableCaptureForms } = useFeatureFlagService()

  const gatedStageMap = useMemo<Map<number, CaptureForm>>(
    () => buildGatedStageMap(captureForms, enableCaptureForms),
    [enableCaptureForms, captureForms]
  )

  return { gatedStageMap, isLoading: enableCaptureForms ? isLoading : false }
}

export function useGetCaptureForm(id: number) {
  return useQuery({
    queryKey: captureFormKey(id),
    queryFn: () => getCaptureForm(id),
    enabled: !!id,
  })
}

export const captureKey = (id: number) => ['captures', id] as const

export function useGetCapture(id: number) {
  return useQuery({
    queryKey: captureKey(id),
    queryFn: () => getCapture(id),
    enabled: !!id,
  })
}

export function useSubmitCapture() {
  // No cache invalidation here: submitting a capture never mutates a
  // CaptureForm. The caller (CaptureFormModal) invalidates the affected
  // deal/company/timeline queries on success.
  return useMutation({
    mutationFn: (payload: CaptureSubmitPayload) => submitCapture(payload),
  })
}

export function useUpdateCapture() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      values,
    }: {
      id: number
      values: Record<string, unknown>
    }) => updateCapture(id, values),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: captureKey(id) })
    },
  })
}

export function useDeleteCapture() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteCapture(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: captureKey(id) })
    },
  })
}

export const ADMIN_CAPTURE_FORMS_KEY = ['admin-capture-forms'] as const

export function useGetAdminCaptureForms() {
  return useQuery({
    queryKey: ADMIN_CAPTURE_FORMS_KEY,
    queryFn: () => listAdminCaptureForms(),
  })
}

export function useCreateAdminCaptureForm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: AdminCaptureFormInput) => createAdminCaptureForm(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAPTURE_FORMS_KEY })
    },
  })
}

export function useUpdateAdminCaptureForm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AdminCaptureFormInput }) =>
      updateAdminCaptureForm(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAPTURE_FORMS_KEY })
    },
  })
}

export function useDeleteAdminCaptureForm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteAdminCaptureForm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAPTURE_FORMS_KEY })
    },
  })
}
